import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, ShieldCheck, Pause, Play, AlertTriangle, CheckCircle2, RefreshCw, Activity } from "lucide-react";
import { toast } from "sonner";

const fmtUk = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function SenseCheckStatus() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [maxCheck, setMaxCheck] = useState(250);
  const [dryRun, setDryRun] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/sense-check/status"); setS(r.data); }
    catch { /* ignore */ }
  };

  const isRunning = !!s?.last_run?.running;

  useEffect(() => { load(); }, []);

  const togglePause = async () => {
    if (!s) return;
    setBusy(true);
    try {
      await api.post("/admin/sense-check/pause", { paused: s.enabled });
      toast.success(s.enabled ? "Sense check paused" : "Sense check resumed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/sense-check/run", {
        max_check: Math.max(1, Math.min(2000, Number(maxCheck) || 250)),
        dry_run: dryRun,
        run_in_background: true,
      });
      toast.success(dryRun ? "Dry-run sweep queued" : "Sweep queued — DB updates as it progresses");
      setTimeout(load, 1500);
      setTimeout(load, 6000);
      setTimeout(load, 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start sweep");
    } finally { setBusy(false); }
  };

  if (!s) return null;
  const last = s.last_run || {};
  const samples = Array.isArray(last.expired_samples) ? last.expired_samples : [];
  const lastWasError = !!last.error_at && !last.finished_at;

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="sense-check-status">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sky" />
            <div className="font-display font-bold text-ink text-lg tracking-tight">Daily vacancy sense check</div>
            <span
              className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded ${s.enabled ? "bg-emerald-100 text-emerald-900" : "bg-stone-200 text-stone-700"}`}
              data-testid="sense-check-enabled-badge"
            >
              {s.enabled ? "Active" : "Paused"}
            </span>
            {isRunning && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded bg-sky-100 text-sky-900 flex items-center gap-1" data-testid="sense-check-running-badge">
                <Activity className="w-3 h-3 animate-pulse" /> Running
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Pings every live vacancy&apos;s apply URL once a day at 03:00 UTC and auto-expires any that the operator has visibly taken down
            (HTTP 404/410 or a closed-vacancy phrase in the page body). Keeps the public feed honest for highly-watched campaigns like trainee train driver roles.
          </p>
          <div className="mt-3 text-sm flex gap-6 flex-wrap">
            <div>Next sweep: <span className="font-mono text-ink" data-testid="sense-check-next-run">{fmtUk(s.next_run_utc)}</span> UK</div>
            <div>Live vacancies: <span className="font-semibold text-ink" data-testid="sense-check-live-count">{s.live_vacancies}</span></div>
            <div>Total auto-expired: <span className="font-semibold text-ink" data-testid="sense-check-auto-expired-total">{s.auto_expired_total}</span></div>
          </div>
        </div>
        <Button
          onClick={togglePause}
          disabled={busy}
          variant={s.enabled ? "outline" : "default"}
          className={s.enabled ? "" : "bg-amber text-ink hover:bg-amber-600 font-semibold"}
          data-testid="sense-check-toggle"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : s.enabled ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
          {s.enabled ? "Pause auto-sweep" : "Resume auto-sweep"}
        </Button>
      </div>

      {/* Last run stats */}
      {(last.finished_at || last.started_at || lastWasError) && (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm" data-testid="sense-check-last-stats">
          <Stat label="Last finished" value={last.finished_at ? fmtUk(last.finished_at) : (last.started_at ? "in progress…" : "—")} />
          <Stat label="Checked" value={last.checked ?? 0} />
          <Stat label="Auto-expired" value={last.auto_expired ?? 0} warn={(last.auto_expired ?? 0) > 0} />
          <Stat label="Unknown / transient" value={last.unknown ?? 0} small />
        </div>
      )}

      {lastWasError && (
        <div className="mt-4 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900" data-testid="sense-check-error">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Last sweep errored</div>
          <div className="font-mono break-all">{last.error || "—"}</div>
        </div>
      )}

      {/* Manual run controls */}
      <div className="mt-5 pt-5 border-t border-border" data-testid="sense-check-manual">
        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-2">Run a manual sweep now</div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="max-w-[180px]">
            <Label className="text-xs">Max vacancies to check</Label>
            <Input type="number" min={1} max={2000} value={maxCheck} onChange={(e) => setMaxCheck(e.target.value)} data-testid="sense-check-max" />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink select-none">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="sense-check-dry-run" />
            Dry-run (no DB writes)
          </label>
          <Button onClick={runNow} disabled={busy || isRunning} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="sense-check-run">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run sweep
          </Button>
          <Button variant="outline" onClick={load} className="border-ink/20" data-testid="sense-check-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          Manual sweeps run in the background — this panel auto-refreshes while one is in progress. The daily auto-sweep at 03:00 UTC scans every live vacancy.
        </div>
      </div>

      {/* Recent auto-expiry samples */}
      {samples.length > 0 && (
        <div className="mt-5 pt-5 border-t border-border" data-testid="sense-check-samples">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> Recently auto-expired ({samples.length}{last.auto_expired > samples.length ? ` of ${last.auto_expired}` : ""})
          </div>
          <div className="bg-paper border border-border rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-sand text-ink">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Title</th>
                  <th className="px-3 py-2 font-semibold">Employer</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Reason</th>
                  <th className="px-3 py-2 font-semibold">URL</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((row, i) => (
                  <tr key={row.id || i} className="border-t border-border align-top">
                    <td className="px-3 py-2 text-ink font-medium">{row.title || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.employer || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.source || "—"}</td>
                    <td className="px-3 py-2 font-mono">{row.status_code ?? "—"}</td>
                    <td className="px-3 py-2 font-mono break-all">{row.reason || "—"}</td>
                    <td className="px-3 py-2"><a href={row.url} target="_blank" rel="noreferrer" className="text-sky underline break-all">{row.url}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, ok, warn, small }) {
  return (
    <div className="bg-paper border border-border rounded p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
        {ok && <CheckCircle2 className="w-3 h-3 text-emerald-700" />}
        {warn && <AlertTriangle className="w-3 h-3 text-amber-700" />}
        {label}
      </div>
      <div className={`mt-1 text-ink font-semibold ${small ? "text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}
